import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { 
    FiSearch, 
    FiRefreshCw, 
    FiUsers, 
    FiEye, 
    FiDownload, 
    FiX, 
    FiCalendar, 
    FiBriefcase, 
    FiFilter, 
    FiMessageSquare, 
    FiChevronLeft, 
    FiChevronRight 
} from 'react-icons/fi';
import CustomSelect from './../CustomSelect';
import { TailSpin } from 'react-loader-spinner';
import './../../Styles/AdminSectionstyles/ReviewedReports.css';

const apiUrl = process.env.REACT_APP_API_URL;

const ROLE_OPTIONS = [
    { value: 'all', label: 'All Roles' },
    { value: 'employee', label: 'Employee' },
    { value: 'lead', label: 'Lead' },
    { value: 'admin', label: 'Admin / Manager' },
    { value: 'hr', label: 'HR' }
];

const PAGE_SIZE_OPTIONS = [
    { value: '5', label: '5' },
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' }
];

const ReviewedReports = ({ onViewDetails }) => {
    const [reports, setReports] = useState([]);
    const [usersMap, setUsersMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter states
    const [selectedRole, setSelectedRole] = useState('all');
    const [selectedProject, setSelectedProject] = useState('all');
    const [selectedQuarter, setSelectedQuarter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Dynamic dropdown lists
    const [projectOptions, setProjectOptions] = useState([{ value: 'all', label: 'All Projects' }]);
    const [quarterOptions, setQuarterOptions] = useState([{ value: 'all', label: 'All Quarters' }]);

    // Quick Goal Preview Modal state
    const [previewReport, setPreviewReport] = useState(null);
    const [previewGoals, setPreviewGoals] = useState([]);
    const [loadingPreview, setLoadingPreview] = useState(false);

    // Fetch all reviewed reports
    const fetchReports = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${apiUrl}/api/reviewed-reports`);
            const data = Array.isArray(res.data) ? res.data : [];
            setReports(data);
        } catch (err) {
            console.error('Error fetching reviewed reports:', err);
            setError('Failed to load reviewed reports. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch projects and quarters for filter dropdowns, and users for name mapping
    useEffect(() => {
        fetchReports();

        // Fetch Users for reliable ID -> Name resolution
        axios.get(`${apiUrl}/api/users`)
            .then(res => {
                const list = Array.isArray(res.data) ? res.data : [];
                const map = new Map();
                list.forEach(u => {
                    const id = (u.employee_number || u.employee_id || '').toLowerCase().trim();
                    const name = u.full_name || u.name;
                    if (id && name) map.set(id, name);
                });
                setUsersMap(map);
            })
            .catch(err => console.error('Error fetching users for name map:', err));

        // Fetch Projects
        axios.get(`${apiUrl}/api/fetch-projects`)
            .then(res => {
                const projects = Array.isArray(res.data) ? res.data : [];
                const pNames = new Set();
                projects.forEach(p => {
                    const name = p.project_name || p.name;
                    if (name) pNames.add(name);
                });
                setProjectOptions([
                    { value: 'all', label: 'All Projects' },
                    ...Array.from(pNames).map(name => ({ value: name, label: name }))
                ]);
            })
            .catch(err => console.error('Error fetching projects for filter:', err));

        // Fetch Quarters
        axios.get(`${apiUrl}/api/quarters`)
            .then(res => {
                const qs = Array.isArray(res.data) ? res.data : [];
                const qNames = new Set();
                qs.forEach(q => {
                    const name = typeof q === 'string' ? q : (q.quarter || q.quarter_name || q.name);
                    if (name) qNames.add(name);
                });
                setQuarterOptions([
                    { value: 'all', label: 'All Quarters' },
                    ...Array.from(qNames).map(name => ({ value: name, label: name }))
                ]);
            })
            .catch(err => console.error('Error fetching quarters for filter:', err));
    }, [fetchReports]);

    const getEmployeeName = useCallback((report) => {
        if (!report) return '';
        const idKey = String(report.employee_id || report.employee_number || '').toLowerCase().trim();
        const hasValidFullName = report.full_name && !report.full_name.toUpperCase().startsWith('TSQS');
        if (hasValidFullName) {
            return report.full_name;
        }
        if (usersMap.has(idKey)) {
            return usersMap.get(idKey);
        }
        return report.full_name || report.name || report.employee_name || report.employee_id || 'Employee';
    }, [usersMap]);

    // Merge any distinct projects or quarters discovered in reports data into filter lists
    useEffect(() => {
        if (!reports.length) return;

        // Projects discovered in reports
        setProjectOptions(prev => {
            const existingValues = new Set(prev.map(p => p.value.toLowerCase()));
            const newOptions = [...prev];
            reports.forEach(r => {
                if (r.project_name && r.project_name !== 'Unassigned' && !existingValues.has(r.project_name.toLowerCase())) {
                    existingValues.add(r.project_name.toLowerCase());
                    newOptions.push({ value: r.project_name, label: r.project_name });
                }
            });
            return newOptions;
        });

        // Quarters discovered in reports
        setQuarterOptions(prev => {
            const existingValues = new Set(prev.map(q => q.value.toLowerCase()));
            const newOptions = [...prev];
            reports.forEach(r => {
                if (r.quarter && !existingValues.has(r.quarter.toLowerCase())) {
                    existingValues.add(r.quarter.toLowerCase());
                    newOptions.push({ value: r.quarter, label: r.quarter });
                }
            });
            return newOptions;
        });
    }, [reports]);

    // Filter reports based on active filters
    const filteredReports = useMemo(() => {
        return reports.filter(item => {
            // Role filter
            if (selectedRole !== 'all') {
                const roleLower = (item.role || '').toLowerCase();
                if (selectedRole === 'admin') {
                    if (roleLower !== 'admin' && roleLower !== 'manager') return false;
                } else if (roleLower !== selectedRole) {
                    return false;
                }
            }

            // Project filter
            if (selectedProject !== 'all') {
                const proj = (item.project_name || '').toLowerCase();
                const target = selectedProject.toLowerCase();
                const hasProjectInArray = Array.isArray(item.project_names) && 
                    item.project_names.some(p => p.toLowerCase() === target);
                if (proj !== target && !hasProjectInArray) return false;
            }

            // Quarter filter
            if (selectedQuarter !== 'all') {
                if ((item.quarter || '').toLowerCase() !== selectedQuarter.toLowerCase()) return false;
            }

            // Search term filter
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const empName = (getEmployeeName(item) || '').toLowerCase();
                const nameMatch = (item.full_name || '').toLowerCase().includes(term) || empName.includes(term);
                const idMatch = (item.employee_id || '').toLowerCase().includes(term);
                const projMatch = (item.project_name || '').toLowerCase().includes(term);
                const commentMatch = (item.reviewer_comment || '').toLowerCase().includes(term) ||
                                     (item.manager_comment || '').toLowerCase().includes(term);
                if (!nameMatch && !idMatch && !projMatch && !commentMatch) return false;
            }

            return true;
        });
    }, [reports, selectedRole, selectedProject, selectedQuarter, searchTerm]);

    // Reset pagination to page 1 whenever filters or pageSize change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedRole, selectedProject, selectedQuarter, searchTerm, pageSize]);

    // Total pages and paginated slice
    const totalPages = Math.ceil(filteredReports.length / pageSize) || 1;
    const paginatedReports = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredReports.slice(startIndex, startIndex + pageSize);
    }, [filteredReports, currentPage, pageSize]);

    // Calculated metrics
    const metrics = useMemo(() => {
        const total = filteredReports.length;
        const uniqueProjects = new Set(filteredReports.map(r => r.project_name).filter(Boolean)).size;

        return { total, uniqueProjects };
    }, [filteredReports]);

    const hasActiveFilters = selectedRole !== 'all' || selectedProject !== 'all' || selectedQuarter !== 'all' || searchTerm.trim() !== '';

    const handleResetFilters = () => {
        setSelectedRole('all');
        setSelectedProject('all');
        setSelectedQuarter('all');
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Open detail dialog
    const handleViewDetails = (report) => {
        if (onViewDetails) {
            const employeeObj = {
                employee_number: report.employee_number || report.employee_id,
                full_name: getEmployeeName(report),
                role: report.role,
                project_name: report.project_name !== 'Unassigned' ? report.project_name : '',
                project_names: report.project_names || [],
                job_title: report.job_title,
                department: report.department,
                location: report.location,
                email: report.email
            };
            onViewDetails(employeeObj, report.quarter);
        } else {
            openQuickPreview(report);
        }
    };

    // Quick Goal Preview
    const openQuickPreview = async (report) => {
        setPreviewReport(report);
        setLoadingPreview(true);
        try {
            const res = await axios.get(`${apiUrl}/api/employee-admin-goals/${report.employee_id}/${report.quarter}`);
            setPreviewGoals(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Error fetching preview goals:', err);
            setPreviewGoals([]);
        } finally {
            setLoadingPreview(false);
        }
    };

    const closeQuickPreview = () => {
        setPreviewReport(null);
        setPreviewGoals([]);
    };

    // Export to CSV
    const handleExportCSV = () => {
        if (!filteredReports.length) return;
        const headers = ['Employee ID', 'Full Name', 'Role', 'Project', 'Quarter', 'Final Rating', 'Evaluation Status', 'Reviewer Comment', 'Review Date'];
        const rows = filteredReports.map(r => [
            `"${r.employee_id || ''}"`,
            `"${getEmployeeName(r)}"`,
            `"${r.role || ''}"`,
            `"${r.project_name || ''}"`,
            `"${r.quarter || ''}"`,
            `"${r.final_rating || 'N/A'}"`,
            `"${r.status || (r.reviewed_goals > 0 ? 'Evaluated' : 'Reviewed')}"`,
            `"${(r.reviewer_comment || r.manager_comment || '').replace(/"/g, '""')}"`,
            `"${r.review_date ? new Date(r.review_date).toLocaleDateString() : ''}"`
        ]);

        const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `Reviewed_Reports_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Initials helper - clean alphabetic only, no parentheses or symbols
    const getInitials = (name, employeeId) => {
        if (!name) {
            if (employeeId) {
                const letters = employeeId.replace(/[^A-Za-z]/g, '');
                if (letters.length >= 2) return letters.substring(0, 2).toUpperCase();
            }
            return 'EM';
        }

        const cleanLetters = name.replace(/[^A-Za-z\s]/g, ' ').trim();
        const words = cleanLetters.split(/\s+/).filter(Boolean);

        if (words.length === 0) {
            if (employeeId) {
                const letters = employeeId.replace(/[^A-Za-z]/g, '');
                if (letters.length >= 2) return letters.substring(0, 2).toUpperCase();
            }
            return 'EM';
        }

        if (words[0].toLowerCase() === 'employee' && words.length > 1) {
            return words[1].substring(0, 2).toUpperCase();
        }

        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase();
        }

        const first = words[0][0];
        const last = words[words.length - 1][0];
        return (first + last).toUpperCase();
    };

    // Generate pagination numbers
    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const startIndex = filteredReports.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, filteredReports.length);

    return (
        <div className="reviewed-reports-container">
            {/* Page Header */}
            <div className="rr-header">
                <div className="rr-title-group">
                    <h1 className="rr-title">Reviewed Reports</h1>
                    <p className="rr-subtitle">
                        Comprehensive record of evaluated goals and performance assessments by Main Manager
                    </p>
                </div>
                <div className="rr-header-actions">
                    <button 
                        className="rr-btn rr-btn-secondary"
                        onClick={handleExportCSV}
                        disabled={filteredReports.length === 0}
                        title="Export filtered reports as CSV"
                    >
                        <FiDownload size={16} />
                        <span>Export CSV</span>
                    </button>
                    <button 
                        className="rr-btn rr-btn-primary"
                        onClick={fetchReports}
                        disabled={loading}
                        title="Refresh reports data"
                    >
                        <FiRefreshCw size={16} className={loading ? 'rr-spin' : ''} />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            {/* Metrics Summary Cards */}
            <div className="rr-metrics-grid">
                <div className="rr-metric-card">
                    <div className="rr-metric-icon rr-icon-blue">
                        <FiUsers size={22} />
                    </div>
                    <div className="rr-metric-content">
                        <span className="rr-metric-label">Total Reviewed</span>
                        <h3 className="rr-metric-value">{metrics.total}</h3>
                        <span className="rr-metric-sub">Assessments completed</span>
                    </div>
                </div>

                <div className="rr-metric-card">
                    <div className="rr-metric-icon rr-icon-amber">
                        <FiBriefcase size={22} />
                    </div>
                    <div className="rr-metric-content">
                        <span className="rr-metric-label">Active Projects</span>
                        <h3 className="rr-metric-value">{metrics.uniqueProjects}</h3>
                        <span className="rr-metric-sub">Represented in reviews</span>
                    </div>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="rr-filters-bar">
                <div className="rr-search-box">
                    <FiSearch className="rr-search-icon" size={17} />
                    <input 
                        type="text" 
                        placeholder="Search employee, ID, project, or comments..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="rr-search-input"
                    />
                    {searchTerm && (
                        <button 
                            className="rr-search-clear-btn" 
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                        >
                            <FiX size={15} />
                        </button>
                    )}
                </div>

                <div className="rr-dropdown-filters">
                    {/* Role Filter */}
                    <div className="rr-filter-item">
                        <label className="rr-filter-label">Role:</label>
                        <CustomSelect 
                            value={selectedRole}
                            onChange={(val) => setSelectedRole(val)}
                            options={ROLE_OPTIONS}
                            placeholder="All Roles"
                            className="rr-select"
                            style={{ minWidth: '150px' }}
                        />
                    </div>

                    {/* Project Filter */}
                    <div className="rr-filter-item">
                        <label className="rr-filter-label">Project:</label>
                        <CustomSelect 
                            value={selectedProject}
                            onChange={(val) => setSelectedProject(val)}
                            options={projectOptions}
                            placeholder="All Projects"
                            className="rr-select"
                            style={{ minWidth: '160px' }}
                        />
                    </div>

                    {/* Quarter Filter ("quatar") */}
                    <div className="rr-filter-item">
                        <label className="rr-filter-label">Quarter:</label>
                        <CustomSelect 
                            value={selectedQuarter}
                            onChange={(val) => setSelectedQuarter(val)}
                            options={quarterOptions}
                            placeholder="All Quarters"
                            className="rr-select"
                            style={{ minWidth: '150px' }}
                        />
                    </div>

                    {/* Reset Filters */}
                    <button 
                        className={`rr-reset-filters-btn ${hasActiveFilters ? 'active' : ''}`}
                        onClick={handleResetFilters}
                        title={hasActiveFilters ? "Reset all filters" : "Reset filters"}
                        aria-label="Reset all filters"
                    >
                        <FiRefreshCw size={15} />
                    </button>
                </div>
            </div>

            {/* Reports Content */}
            {loading ? (
                <div className="rr-loading-container">
                    <TailSpin height="50" width="50" color="var(--brand, #F59E0B)" ariaLabel="loading" />
                    <p className="rr-loading-text">Loading reviewed reports...</p>
                </div>
            ) : error ? (
                <div className="rr-error-state">
                    <p>{error}</p>
                    <button className="rr-btn rr-btn-primary" onClick={fetchReports}>Try Again</button>
                </div>
            ) : filteredReports.length === 0 ? (
                <div className="rr-empty-state">
                    <div className="rr-empty-icon">
                        <FiFilter size={40} />
                    </div>
                    <h3>No Reviewed Reports Found</h3>
                    <p>
                        {hasActiveFilters 
                            ? 'No evaluation records match your current filter selection.' 
                            : 'No reviewed goals have been submitted yet.'}
                    </p>
                    {hasActiveFilters && (
                        <button className="rr-btn rr-btn-secondary" onClick={handleResetFilters}>
                            Clear Filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="rr-table-wrapper">
                    <table className="rr-table">
                        <thead>
                            <tr>
                                <th>Employee</th>
                                <th>Role</th>
                                <th>Project</th>
                                <th>Quarter</th>
                                <th>Evaluation Status</th>
                                <th className="rr-th-action">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedReports.map((report) => {
                                const employeeName = getEmployeeName(report);
                                const initials = getInitials(employeeName);

                                return (
                                    <tr key={report._id || `${report.employee_id}-${report.quarter}`} className="rr-row">
                                        {/* Employee Info - Show Name only */}
                                        <td className="rr-td-employee">
                                            <div className="rr-emp-cell">
                                                <div className="rr-avatar" title={employeeName}>
                                                    {initials}
                                                </div>
                                                <div className="rr-emp-meta">
                                                    <span className="rr-emp-name" title={employeeName}>
                                                        {employeeName}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Role - Neutral without colors */}
                                        <td>
                                            <span className="rr-role-text">
                                                {report.role === 'admin' ? 'Manager' : (report.role ? report.role.charAt(0).toUpperCase() + report.role.slice(1) : 'Employee')}
                                            </span>
                                        </td>

                                        {/* Project - Without symbol/icon */}
                                        <td>
                                            <span className="rr-project-text" title={report.project_name}>
                                                {report.project_name || 'Unassigned'}
                                            </span>
                                        </td>

                                        {/* Quarter */}
                                        <td>
                                            <span className="rr-quarter-badge">
                                                <FiCalendar size={12} className="rr-badge-icon" />
                                                {report.quarter}
                                            </span>
                                        </td>

                                        {/* Evaluation Status */}
                                        <td>
                                            <div className="rr-goals-status">
                                                <span className="rr-status-text">
                                                    {report.status || (report.reviewed_goals > 0 ? 'Evaluated' : 'Reviewed')}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Action Button */}
                                        <td className="rr-td-action">
                                            <button 
                                                className="rr-view-btn"
                                                onClick={() => handleViewDetails(report)}
                                                title="View detailed goal assessment"
                                            >
                                                <FiEye size={14} />
                                                <span>View Details</span>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Pagination Bar */}
                    <div className="rr-pagination-bar">
                        <div className="rr-pagination-controls">
                            <button 
                                className="rr-page-nav-btn"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                title="Previous page"
                                aria-label="Previous page"
                            >
                                <FiChevronLeft size={16} />
                            </button>

                            <div className="rr-page-numbers">
                                {getPageNumbers().map((pageNum, idx) => (
                                    pageNum === '...' ? (
                                        <span key={`dots-${idx}`} className="rr-page-dots">...</span>
                                    ) : (
                                        <button
                                            key={`page-${pageNum}`}
                                            className={`rr-page-num-btn ${currentPage === pageNum ? 'active' : ''}`}
                                            onClick={() => setCurrentPage(pageNum)}
                                        >
                                            {pageNum}
                                        </button>
                                    )
                                ))}
                            </div>

                            <button 
                                className="rr-page-nav-btn"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                title="Next page"
                                aria-label="Next page"
                            >
                                <FiChevronRight size={16} />
                            </button>
                        </div>

                        <div className="rr-pagination-right">
                            <div className="rr-page-size-selector">
                                <span className="rr-page-size-label">Rows per page:</span>
                                <CustomSelect
                                    value={String(pageSize)}
                                    onChange={(val) => setPageSize(Number(val))}
                                    options={PAGE_SIZE_OPTIONS}
                                    className="rr-page-size-select"
                                    style={{ minWidth: '76px' }}
                                />
                            </div>
                            <div className="rr-pagination-info">
                                Showing <strong>{startIndex}</strong> to <strong>{endIndex}</strong> of <strong>{filteredReports.length}</strong> reports
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Preview Modal (Fallback if onViewDetails opens inline) */}
            {previewReport && (
                <div className="rr-preview-overlay" onClick={closeQuickPreview}>
                    <div className="rr-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="rr-preview-header">
                            <div>
                                <h3>Goals Assessment - {getEmployeeName(previewReport)}</h3>
                                <p className="rr-preview-meta">
                                    <span>ID: {previewReport.employee_id}</span> • 
                                    <span>Quarter: {previewReport.quarter}</span> • 
                                    <span>Project: {previewReport.project_name}</span>
                                </p>
                            </div>
                            <button className="rr-close-btn" onClick={closeQuickPreview}>
                                <FiX size={18} />
                            </button>
                        </div>

                        <div className="rr-preview-body">
                            {loadingPreview ? (
                                <div className="rr-loading-container" style={{ padding: '30px' }}>
                                    <TailSpin height="36" width="36" color="var(--brand, #F59E0B)" />
                                    <p>Loading goal details...</p>
                                </div>
                            ) : previewGoals.length === 0 ? (
                                <p className="rr-no-goals">No individual goals found for this assessment period.</p>
                            ) : (
                                <div className="rr-preview-goals-list">
                                    {previewGoals.map((g, idx) => (
                                        <div key={g._id || idx} className="rr-preview-goal-card">
                                            <div className="rr-goal-head">
                                                <span className="rr-goal-number">Goal #{idx + 1}</span>
                                                {g.reviewer_rating && (
                                                    <span className="rr-goal-rating">
                                                        Rating: {g.reviewer_rating}/5
                                                    </span>
                                                )}
                                            </div>
                                            <p className="rr-goal-desc">{g.description || g.goal_description || 'Goal Assessment'}</p>
                                            {g.self_assessment && (
                                                <div className="rr-goal-assessment">
                                                    <strong>Self Assessment:</strong> {g.self_assessment}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {previewReport.reviewer_comment && (
                                <div className="rr-preview-comment-box">
                                    <div className="rr-comment-head">
                                        <FiMessageSquare size={14} /> Reviewer Comment:
                                    </div>
                                    <p className="rr-comment-content">{previewReport.reviewer_comment}</p>
                                </div>
                            )}
                        </div>


                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewedReports;
